import { Deferred } from '@helo/utils';
import { isEqual } from 'lodash';
import { configMatchesMatcher } from '../DateTypeConfig/DataTypeConfig.utils';
import { DataTypeSelection } from '../DateTypeConfig/DataTypeSelection';
const cascadeData = (data, updaterFunc, areEqual) => (dataTypeConfig) => {
    if (areEqual(data, dataTypeConfig)) {
        return dataTypeConfig;
    }
    const isObject = typeof data === 'object' && data !== null;
    const updatedDataType = updaterFunc(data, dataTypeConfig);
    switch (dataTypeConfig.kind) {
        case 'INPUT_VALUE':
        case 'FIELD':
            return {
                ...updatedDataType,
                ofType: cascadeData(data, updaterFunc, areEqual)(dataTypeConfig.ofType),
            };
        case 'OBJECT':
        case 'INPUT_OBJECT':
            const fields = dataTypeConfig.fields.map(field => {
                return cascadeData(isObject ? data[field.key] : undefined, updaterFunc, areEqual)(field);
            });
            return {
                ...updatedDataType,
                fields,
            };
        case 'LIST':
            const items = Array.isArray(data)
                ? data.map(item => cascadeData(item, updaterFunc, areEqual)(dataTypeConfig.ofType))
                : undefined;
            return {
                ...updatedDataType,
                items,
            };
        default:
            return updatedDataType;
    }
};
const setInitialVariables = (variables) => (inputValues) => inputValues.map(inputValueConfig => {
    const variableData = variables[inputValueConfig.key];
    const areEqual = (data, config) => isEqual(data, config.state?.initialValue);
    const updaterFunc = (data, dataTypeConfig) => ({
        ...dataTypeConfig,
        state: Deferred.merge({ initialValue: data })(dataTypeConfig.state ?? {}),
    });
    return cascadeData(variableData, updaterFunc, areEqual)(inputValueConfig);
});
const resetVariables = (inputValues) => {
    return inputValues;
};
const updateVariables = (variables) => (inputValues) => {
    return inputValues.map(inputValueConfig => {
        if (variables.hasOwnProperty(inputValueConfig.key)) {
            const variableData = variables[inputValueConfig.key];
            const areEqual = (data, config) => isEqual(data, config.state?.asValue);
            const updaterFunc = (data, dataTypeConfig) => ({
                ...dataTypeConfig,
                state: Deferred.merge({ asValue: data })(dataTypeConfig.state ?? {}),
            });
            return cascadeData(variableData, updaterFunc, areEqual)(inputValueConfig);
        }
        return inputValueConfig;
    });
};
const updateVariable = (locator, value) => (dataTypeConfig) => {
    if (locator.length === 0) {
        const newConfig = {
            ...dataTypeConfig,
            state: Deferred.merge({ asValue: value })(dataTypeConfig.state ?? {}),
        };
        if (newConfig.kind === 'LIST' && Array.isArray(value)) {
            const listConfig = newConfig;
            listConfig.items = value.map((itemValue, i) => {
                const ofType = listConfig.ofType;
                const basePath = listConfig.meta?.path ?? [];
                return {
                    ...ofType,
                    state: Deferred.merge({ asValue: itemValue })(ofType.state ?? {}),
                    meta: {
                        path: [...basePath, ['key', i.toString()]],
                        unmappedPath: listConfig.meta?.unmappedPath ?? listConfig.meta?.path ?? [],
                    },
                };
            });
        }
        return newConfig;
    }
    const [currentMatcher, ...restLocator] = locator;
    switch (dataTypeConfig.kind) {
        case 'INPUT_VALUE':
        case 'FIELD': {
            if (!configMatchesMatcher(currentMatcher)(dataTypeConfig)) {
                return dataTypeConfig;
            }
            const newOfType = updateVariable(restLocator, value)(dataTypeConfig.ofType);
            if (newOfType === dataTypeConfig.ofType) {
                return dataTypeConfig;
            }
            const newState = Deferred.merge({
                asValue: newOfType.state?.asValue,
            })(dataTypeConfig.state ?? {});
            return { ...dataTypeConfig, ofType: newOfType, state: newState };
        }
        case 'INPUT_OBJECT':
        case 'OBJECT': {
            const fields = dataTypeConfig.fields;
            let wasChanged = false;
            const newFields = fields.map(field => {
                const updatedField = updateVariable(locator, value)(field);
                if (updatedField !== field) {
                    wasChanged = true;
                }
                return updatedField;
            });
            if (!wasChanged) {
                return dataTypeConfig;
            }
            const newObjectValue = Deferred.merge({})(dataTypeConfig.state?.asValue ?? {});
            newFields.forEach(field => {
                if ('key' in field && 'asValue' in (field.state ?? {})) {
                    newObjectValue[field.key] = field.state.asValue;
                }
            });
            const newState = Deferred.merge({ asValue: newObjectValue })(dataTypeConfig.state ?? {});
            if (dataTypeConfig.kind === 'OBJECT') {
                return {
                    ...dataTypeConfig,
                    fields: newFields,
                    state: newState,
                };
            }
            return {
                ...dataTypeConfig,
                fields: newFields,
                state: newState,
            };
        }
        case 'LIST': {
            const [matcherType, matcherValue] = currentMatcher;
            if (matcherType !== 'index') {
                throw new Error('Attempting to set a value within a list without specifying its index.');
            }
            const index = matcherValue;
            const items = dataTypeConfig.items ?? [];
            if (index > items.length) {
                throw new Error(`Cannot update list at index ${index}. Index is out of bounds. The current length is ${items.length}.`);
            }
            const newItems = [...items];
            if (index === newItems.length) {
                const basePath = dataTypeConfig.meta?.path ?? [];
                newItems.push({
                    ...dataTypeConfig.ofType,
                    state: Deferred.merge({ asValue: undefined })(dataTypeConfig.ofType.state ?? {}),
                    meta: {
                        path: [...basePath, ['key', newItems.length.toString()]],
                        unmappedPath: dataTypeConfig.meta?.unmappedPath ??
                            dataTypeConfig.meta?.path ??
                            [],
                    },
                });
            }
            const updatedItem = updateVariable(restLocator, value)(newItems[index]);
            if (updatedItem === newItems[index]) {
                return dataTypeConfig;
            }
            newItems[index] = updatedItem;
            const newListValue = newItems.map(item => item.state?.asValue);
            const newState = Deferred.merge({ asValue: newListValue })(dataTypeConfig.state ?? {});
            return { ...dataTypeConfig, items: newItems, state: newState };
        }
        default:
            return dataTypeConfig;
    }
};
const applyVariablePaths = () => (inputValues) => {
    const applyPathToNode = (config, path) => {
        switch (config.kind) {
            case 'INPUT_VALUE':
            case 'FIELD':
                return {
                    ...config,
                    meta: { path, unmappedPath: path },
                    ofType: applyPathToNode(config.ofType, path),
                };
            case 'INPUT_OBJECT':
                return {
                    ...config,
                    meta: { path, unmappedPath: path },
                    fields: config.fields.map(field => {
                        const fieldPath = [...path, ['key', field.key]];
                        return applyPathToNode(field, fieldPath);
                    }),
                };
            case 'OBJECT':
                return {
                    ...config,
                    meta: { path, unmappedPath: path },
                    fields: config.fields.map(field => {
                        const fieldPath = [...path, ['key', field.key]];
                        return applyPathToNode(field, fieldPath);
                    }),
                };
            case 'LIST':
                return {
                    ...config,
                    meta: { path, unmappedPath: path },
                    ofType: applyPathToNode(config.ofType, path),
                    items: config.items?.map((item, index) => {
                        const itemPath = [...path, ['index', index]];
                        return applyPathToNode(item, itemPath);
                    }),
                };
            default:
                return { ...config, meta: { path, unmappedPath: path } };
        }
    };
    return inputValues.map(inputValue => {
        const path = [['key', inputValue.key]];
        return applyPathToNode(inputValue, path);
    });
};
const buildVariables = (config) => {
    const { asValue, initialValue, inferredValue } = config.state ?? {};
    const hasUserSetValue = asValue !== undefined && asValue !== initialValue;
    switch (config.kind) {
        case 'INPUT_OBJECT': {
            let hasDescendantWithValue = false;
            const childVariables = config.fields.reduce((acc, field) => {
                const [fieldVars, childHasValue] = buildVariables(field);
                if (childHasValue) {
                    hasDescendantWithValue = true;
                }
                return { ...acc, ...fieldVars };
            }, {});
            if (hasDescendantWithValue && inferredValue) {
                return [{ ...inferredValue, ...childVariables }, true];
            }
            if (Object.keys(childVariables).length) {
                return [childVariables, true];
            }
            break;
        }
        case 'LIST': {
            let hasDescendantWithValue = false;
            const listVariables = config.items
                ?.map(item => {
                const [itemVars, itemHasValue] = buildVariables(item);
                if (itemHasValue) {
                    hasDescendantWithValue = true;
                }
                return itemVars;
            })
                .filter(v => v !== undefined) ?? [];
            if (hasDescendantWithValue && listVariables?.length) {
                return [listVariables, true];
            }
            break;
        }
        case 'INPUT_VALUE': {
            const [vars, hasDescendantValue] = buildVariables(config.ofType);
            if (hasDescendantValue && vars !== undefined) {
                return [{ [config.key]: vars }, true];
            }
        }
    }
    if (hasUserSetValue) {
        if (config.kind === 'INPUT_VALUE') {
            return [{ [config.key]: asValue }, true];
        }
        return [asValue, true];
    }
    return [undefined, false];
};
const getVariablesFromInputValues = (inputValueConfigs, value) => {
    return inputValueConfigs.reduce((acc, config) => {
        return { ...acc, [config.key]: config.state?.asValue };
    }, {});
};
const getVariablesAsObject = (inputValueConfigs) => {
    const topLevelValues = inputValueConfigs.reduce((acc, config) => {
        if (config.state?.asValue !== undefined) {
            acc[config.key] = config.state.asValue;
        }
        return acc;
    }, {});
    if (Object.keys(topLevelValues).length > 0) {
        const selection = DataTypeSelection.createSelectionFromValue(topLevelValues);
        const selectedConfigs = DataTypeSelection.getSelection(selection)(inputValueConfigs);
        return getVariablesFromInputValues(selectedConfigs, topLevelValues);
    }
    return inputValueConfigs.reduce((acc, config) => {
        const [variables] = buildVariables(config);
        return { ...acc, ...variables };
    }, {});
};
const cascadeDataToContentNode = (data, contentNode) => {
    if (data === undefined || data === null) {
        return { ...contentNode, data };
    }
    const isObject = typeof data === 'object' && data !== null;
    switch (contentNode.kind) {
        case 'INPUT_VALUE': {
            const updatedOfType = cascadeDataToContentNode(data, contentNode.ofType);
            return {
                ...contentNode,
                data,
                ofType: updatedOfType,
            };
        }
        case 'INPUT_OBJECT': {
            const updatedInputFields = contentNode.fields.map(inputField => {
                const fieldKey = 'key' in inputField ? inputField.key : undefined;
                const fieldData = isObject && fieldKey ? data[fieldKey] : undefined;
                return cascadeDataToContentNode(fieldData, inputField);
            });
            return {
                ...contentNode,
                data,
                fields: updatedInputFields,
            };
        }
        case 'LIST': {
            if (Array.isArray(data)) {
                const updatedItems = data.map((itemData, index) => {
                    const existingItem = contentNode.items?.[index];
                    if (existingItem) {
                        return cascadeDataToContentNode(itemData, existingItem);
                    }
                    return cascadeDataToContentNode(itemData, contentNode.ofType);
                });
                return {
                    ...contentNode,
                    data,
                    items: updatedItems,
                };
            }
            return { ...contentNode, data };
        }
        case 'SCALAR':
        case 'ENUM':
            return { ...contentNode, data };
        default:
            return { ...contentNode, data };
    }
};
const setInitialVariablesOnContentNodes = (variables) => (contentNodes) => contentNodes.map(contentNode => {
    if ('key' in contentNode) {
        const variableData = variables[contentNode.key];
        return cascadeDataToContentNode(variableData, contentNode);
    }
    return contentNode;
});
const updateVariablesOnContentNodes = (variables) => (contentNodes) => {
    return contentNodes.map(contentNode => {
        if ('key' in contentNode && variables.hasOwnProperty(contentNode.key)) {
            const variableData = variables[contentNode.key];
            return cascadeDataToContentNode(variableData, contentNode);
        }
        return contentNode;
    });
};
const updateVariableOnContentNode = (locator, value) => (contentNode) => {
    if (locator.length === 0) {
        return cascadeDataToContentNode(value, contentNode);
    }
    const [currentMatcher, ...restLocator] = locator;
    switch (contentNode.kind) {
        case 'INPUT_VALUE': {
            if ('key' in contentNode) {
                const [matcherType, matcherValue] = currentMatcher;
                if (matcherType === 'key' && matcherValue === contentNode.key) {
                    const newOfType = updateVariableOnContentNode(restLocator, value)(contentNode.ofType);
                    if (newOfType !== contentNode.ofType) {
                        return {
                            ...contentNode,
                            ofType: newOfType,
                            data: newOfType.data,
                        };
                    }
                }
            }
            return contentNode;
        }
        case 'INPUT_OBJECT': {
            const [matcherType, matcherValue] = currentMatcher;
            if (matcherType === 'key') {
                let wasChanged = false;
                const newInputFields = contentNode.fields.map(field => {
                    const updatedField = updateVariableOnContentNode(locator, value)(field);
                    if (updatedField !== field) {
                        wasChanged = true;
                    }
                    return updatedField;
                });
                if (wasChanged) {
                    const newData = newInputFields.reduce((acc, field) => {
                        if ('key' in field) {
                            acc[field.key] = field.data;
                        }
                        return acc;
                    }, {});
                    return {
                        ...contentNode,
                        fields: newInputFields,
                        data: newData,
                    };
                }
            }
            return contentNode;
        }
        case 'LIST': {
            const [matcherType, matcherValue] = currentMatcher;
            if (matcherType === 'index') {
                const index = matcherValue;
                const items = contentNode.items ?? [];
                if (index >= 0 && index < items.length) {
                    const updatedItem = updateVariableOnContentNode(restLocator, value)(items[index]);
                    if (updatedItem !== items[index]) {
                        const newItems = [...items];
                        newItems[index] = updatedItem;
                        const newData = newItems.map(item => item.data);
                        return {
                            ...contentNode,
                            items: newItems,
                            data: newData,
                        };
                    }
                }
            }
            return contentNode;
        }
        default:
            return contentNode;
    }
};
const getVariablesFromContentNodes = (contentNodes) => {
    return contentNodes.reduce((acc, contentNode) => {
        if ('key' in contentNode) {
            acc[contentNode.key] = contentNode.data;
        }
        return acc;
    }, {});
};
export const QueryVariablesUtils = {
    setInitialVariables,
    updateVariables,
    resetVariables,
    updateVariable,
    applyVariablePaths,
    getVariablesAsObject,
    setInitialVariablesOnContentNodes,
    updateVariablesOnContentNodes,
    updateVariableOnContentNode,
    getVariablesFromContentNodes,
};
//# sourceMappingURL=QueryVariablesUtils.js.map